import React, { useEffect, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import * as A from 'pages/components/AuthShell/AuthShell.styled';
import * as S from './SignInModal.styled';
import Loader from 'pages/components/Loader/Loader';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useCreds } from 'redux/global';
import { isValidatedEmail } from 'utils';
import {
  selectIsScrollScreen,
  selectIsStaySignedIn,
  selectPassword,
  selectUserEmail,
  setIsScrollScreen,
  setIsStaySignedIn as setSavedIsStaySignedIn,
  setUserEmail,
  setPassword as setUserPassword,
} from 'pages/Hospitality/redux';
import { toast } from 'utils/toast';
import { useHistory } from 'react-router-dom';
import { getLogInBranches, setIsStaff } from 'pages/SignIn/redux';
import { selectHotelData, setHotelData } from 'pages/redux';
import { getHotel, hotelFromGetHotelData } from 'pages/Navigation/redux';
import { usePath } from 'pages/hooks';
import { isHospitalityPagePath } from 'pages/Layout/hospitalityModulePaths';
import { purgeHotelDbCache } from 'utils/dbIndexedCache';
import { ensureHtmlBootSplash, markLogoSplashForSignIn } from 'pages/components/Loader/BootSplash';

export type HotelsData = {
  name: string,
  id: string,
}

const SignInModal = ({
  onExit,
  setIsLoading,
  variant = 'modal',
}:{
  onExit: Function,
  setIsLoading?: Function,
  variant?: 'page' | 'modal',
}) => {
  const savedIsStaySignedIn = useAppSelector(selectIsStaySignedIn);
  const savedEmail = useAppSelector(selectUserEmail);
  const savedPassword = useAppSelector(selectPassword);
  const [isStaySignedIn] = useState<boolean>(savedIsStaySignedIn);
  const [email, setEmail] = useState<string>(isStaySignedIn ? savedEmail : '');
  const [password, setPassword] = useState<string>(isStaySignedIn ? savedPassword : '');
  const [passType, setPassType] = useState<string>('password');
  const [loading, setLoading1] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>();
  const [step, setStep] = useState<number>(1);
  const [hotelsData, setHotelsData] = useState<HotelsData[]>();
  const [selectedHotelId, setSelectedHotelId] = useState<string>();
  const [isSelectHotelError, setIsSelectHotelError] = useState<boolean>(false);
  const isScrollAppScreen = useAppSelector(selectIsScrollScreen);
  const hotelData = useAppSelector(selectHotelData);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { logout, login } = useCreds();
  const { navigateToHome } = usePath();

  const setLoading = (bool: boolean) => {
    setIsLoading?.(bool);
    setLoading1(bool);
  };

  const finalLogIn = async (sId?: string) => {
    if (!isValidatedEmail(email)) {
      setEmailError('Email is invalid!');
      return;
    }
    setLoading(true);
    dispatch(logout());

    const hotelId = await dispatch(
      login({ email: email.toLowerCase(), password, hotelId: sId || selectedHotelId })
    );

    if (hotelId) {
      await purgeHotelDbCache(String(hotelId));
      const res = await dispatch(getHotel(String(hotelId), true));
      setLoading(false);
      if (res.status === 'success') {
        markLogoSplashForSignIn();
        ensureHtmlBootSplash();
        const hotel = hotelFromGetHotelData(res.data);
        if (!hotel) {
          toast('Could not load hotel', { type: 'error' });
          return;
        }
        dispatch(setHotelData(hotel));
        dispatch(setSavedIsStaySignedIn(isStaySignedIn));
        dispatch(setUserEmail(email.toLowerCase()));
        if (isStaySignedIn) {
          dispatch(setUserPassword(password));
        } else {
          dispatch(setUserPassword(''));
        }
        dispatch(setIsStaff(true));
        toast('Succesfully logged in', { type: 'success' });
        return;
      }
      toast(res.data || 'Could not load hotel', { type: 'error' });
      return;
    }
    setLoading(false);
  };

  const onClickLogIn0 = async () => {
    setLoading(true);

    const res = await dispatch(getLogInBranches({ email: email.toLowerCase(), password }));

    if (res.status === 'success') {
      if (res.data.length === 1) {
        finalLogIn(res.data[0].id);
        return;
      }
      // Multiple hotels — pick branch; do not toast the hotels array (shows as [object Object])
      setHotelsData(res.data);
      setStep(2);
      setLoading(false);
      return;
    }
    toast(res.data || 'Could not sign in', { type: 'error' });
    setLoading(false);
  };

  useEffect(() => {
    if (Object.keys(hotelData || {}).length) {
      if (isHospitalityPagePath(window.location.pathname, 'operations')) {
        dispatch(setIsScrollScreen(!isScrollAppScreen));
        onExit();
      } else {
        navigateToHome();
        onExit();
      }
    }
  }, [hotelData]);

  const Step1 = () => (
    <>
      <A.Field>
        Email
        <A.Input
          type="email"
          autoComplete="email"
          value={email}
          $error={Boolean(emailError)}
          placeholder="you@hotel.com"
          onChange={(e) => {
            setEmail(e.target.value.toLowerCase());
            if (e.target.value.toLowerCase() === '' || isValidatedEmail(e.target.value.toLowerCase())) {
              setEmailError('');
            } else {
              setEmailError('Email is invalid!');
            }
          }}
        />
        {emailError ? <A.Error>{emailError}</A.Error> : null}
      </A.Field>

      <A.Field>
        Password
        <A.PasswordWrap>
          <A.Input
            type={passType === 'password' ? 'password' : 'text'}
            autoComplete="current-password"
            value={password}
            placeholder="Your password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <A.EyeBtn
            type="button"
            aria-label="toggle password visibility"
            onClick={() => setPassType(passType === 'password' ? 'text' : 'password')}
          >
            {passType === 'password' ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
          </A.EyeBtn>
        </A.PasswordWrap>
      </A.Field>

      <A.LinkRow>
        <A.TextLink
          type="button"
          onClick={() => {
            history.push('/forgot');
            onExit();
          }}
        >
          Forgot password
        </A.TextLink>
      </A.LinkRow>

      <A.PrimaryBtn type="button" onClick={() => onClickLogIn0()}>
        Log in
      </A.PrimaryBtn>
    </>
  );

  const onClickStart = () => {
    setIsSelectHotelError(false);
    if (!selectedHotelId) {
      setIsSelectHotelError(true);
      toast('Please select branch', { type: 'warning' });
      return;
    }
    finalLogIn();
  };

  const Step2 = () => (
    <>
      <A.Hint>Select the hotel branch you want to open.</A.Hint>
      <A.Field>
        Hotel branch
        <A.Select
          $error={isSelectHotelError}
          defaultValue=""
          onChange={(e) => {
            setIsSelectHotelError(false);
            const thisHotelData = hotelsData?.find((d) => d.name === e.target.value);
            if (thisHotelData) {
              setSelectedHotelId(thisHotelData.id);
            }
          }}
        >
          <option value="" disabled>Select hotel branch</option>
          {hotelsData?.map((d) => (
            <option key={`hData_${d.name}_${d.id}`} value={d.name} selected={selectedHotelId === d.id}>
              {d.name}
            </option>
          ))}
        </A.Select>
      </A.Field>
      <A.BtnRow>
        <A.GhostBtn
          type="button"
          onClick={() => {
            setSelectedHotelId('');
            setIsSelectHotelError(false);
            setStep(1);
          }}
        >
          Back
        </A.GhostBtn>
        <A.PrimaryBtn type="button" onClick={() => onClickStart()}>
          Continue
        </A.PrimaryBtn>
      </A.BtnRow>
    </>
  );

  const body = (
    <>
      <A.FormTitle>{step === 1 ? 'Sign in' : 'Choose branch'}</A.FormTitle>
      <A.FormSub>
        {step === 1
          ? 'Use your staff email and password to access LodgeFirst.'
          : 'Your account is linked to more than one property.'}
      </A.FormSub>
      {step === 1 ? Step1() : Step2()}
    </>
  );

  if (loading) {
    return <Loader isSticky message={step === 1 ? 'Signing in…' : 'Opening branch…'} />;
  }

  if (variant === 'page') {
    return <S.PageForm>{body}</S.PageForm>;
  }

  return (
    <S.ModalCard>
      {body}
    </S.ModalCard>
  );
};

export default SignInModal;
